import request from '../../library/request';
import store from '../../store';
import setGlobalState from '../setGlobalState';
import {wrapXHR} from '../../functions/wrapXHR';
import {XHR_LOADING_STATE} from '../../assets/constants/constants';
import type {AgentResponse, AiAgentAsk, AiAgentChatLang, AiAgentExecuteResult, AiAgentFormId, AiAgentIntent, AiAgentMeta, AiAgentPendingProposal} from './aiAgent.type';

/**
 * The owner the turn runs under: an event route needs its eid, anything else the pid. With
 * neither there is nothing to address, and a /package/null request would only fail.
 */
const getAgentScopePath = (
    scope: 'account' | 'event',
    eid?: number | null,
    pid?: number | null,
): string | null => {
    if(scope === 'event' && eid) {
        return `/event/${eid}`;
    }
    return pid ? `/package/${pid}` : null;
};

export type SendAiAgentMessageParams = {
    prompt: string;
    history: unknown[];
    sessionId: string;
    scope: 'account' | 'event';
    currentRoute: string;
    eid?: number | null;
    pid?: number | null;
    formId?: AiAgentFormId | null;
    eventType?: string | null;
    sessionIntent?: AiAgentIntent | null;
    chatLang?: AiAgentChatLang | null;
    currentFormValues?: Record<string, unknown> | null;
    /** The ask this message answers — lets the server resolve a numbered reply. */
    pendingAsk?: AiAgentAsk | null;
    /** The event open here is the one this chat created, so a change to it can update it. */
    recordCreated?: boolean;
    feature?: string;
    /** data:image/...;base64,... for vision — current turn only */
    imageDataUrl?: string;
    /** data:application/pdf;base64,... for document reading — current turn only */
    fileDataUrl?: string;
    fileName?: string;
};

export const sendAiAgentMessage = ({
    prompt,
    history,
    sessionId,
    scope,
    currentRoute,
    eid,
    pid,
    formId = null,
    eventType = null,
    sessionIntent = null,
    chatLang = null,
    currentFormValues = null,
    pendingAsk = null,
    recordCreated = false,
    feature = 'aiAgent',
    imageDataUrl,
    fileDataUrl,
    fileName,
}: SendAiAgentMessageParams): Promise<AgentResponse | null> =>
    wrapXHR('aiAgent.sendMessageXHR', async () => {
        // agentSessionMemory reads the catalog too — retry a fetch that failed on panel open.
        void loadAiAgentMeta();
        const scopePath = getAgentScopePath(scope, eid, pid);
        if(!scopePath) {
            return null;
        }
        const body = {
            prompt,
            history,
            sessionId,
            feature,
            scope,
            currentRoute,
            ...(formId ? {formId} : {}),
            ...(eventType ? {eventType} : {}),
            ...(sessionIntent ? {sessionIntent} : {}),
            ...(chatLang ? {chatLang} : {}),
            ...(currentFormValues && Object.keys(currentFormValues).length
                ? {currentFormValues}
                : {}),
            ...(imageDataUrl ? {imageDataUrl} : {}),
            ...(fileDataUrl ? {fileDataUrl, ...(fileName ? {fileName} : {})} : {}),
            ...(pendingAsk?.options?.length ? {pendingAsk} : {}),
            ...(recordCreated ? {recordCreated} : {}),
        };

        const res = await request(
            'aiAgent.sendMessage',
            'post',
            `${scopePath}/aiAgentMessage`,
            body,
            {handleError: true},
        );

        const agentPayload = res?.agentPayload as AgentResponse | undefined;
        if(!agentPayload || typeof agentPayload.text !== 'string' || !agentPayload.uiAction?.type) {
            return null;
        }
        return agentPayload;
    });

export type ExecuteAiAgentProposalParams = {
    sessionId: string;
    scope: 'account' | 'event';
    eid?: number | null;
    pid?: number | null;
    proposal: AiAgentPendingProposal;
};

export const executeAiAgentProposal = ({
    sessionId,
    scope,
    eid,
    pid,
    proposal,
}: ExecuteAiAgentProposalParams): Promise<AiAgentExecuteResult | null> =>
    wrapXHR('aiAgent.executeProposalXHR', async () => {
        const scopePath = getAgentScopePath(scope, eid, pid);
        if(!scopePath) {
            return null;
        }
        const body = {sessionId, scope, proposal};
        const res = await request(
            'aiAgent.executeProposal',
            'post',
            `${scopePath}/aiAgentExecute`,
            body,
            {handleError: true},
        );
        const result = res?.result as AiAgentExecuteResult | undefined;
        if(!result?.eid || !result.intent) {
            return null;
        }
        return result;
    });

/**
 * The server-owned intent catalog (suggestions, form ids, confirm labels). Fetched once per
 * package; on failure request() shows its generic toast and the next panel open or sent
 * message retries — until then the catalog-driven UI waits.
 */
let inFlightPid: number | null = null;

export const loadAiAgentMeta = async (): Promise<void> => {
    const {pid} = getAiAgentContext();
    const {meta, metaPid, metaXHR} = store.getState().aiAgent ?? {};
    if(!pid || metaXHR === XHR_LOADING_STATE || (meta && metaPid === pid)) {
        return;
    }
    inFlightPid = pid;
    const res = await request('aiAgent.meta', 'get', `/package/${pid}/aiAgentMeta`);
    const fetched = res?.aiAgentMeta as AiAgentMeta | undefined;
    if(fetched?.intents?.length) {
        setGlobalState('aiAgent.meta', fetched);
        setGlobalState('aiAgent.metaPid', pid);
    }
    // If the user switched packages while this fetch was in flight, retry for the new package.
    if(getAiAgentContext().pid !== inFlightPid) {
        void loadAiAgentMeta();
    }
};

export const getAiAgentContext = () => {
    const state = store.getState();
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const eventMatch = pathname.match(/\/e\/(\d+)/);
    const packageMatch = pathname.match(/\/p\/(\d+)/);
    // Only use eid from the URL — stale event.eid in Redux must not be sent on account pages
    // (permission middleware would check the wrong event and return 500).
    const eid = eventMatch ? Number(eventMatch[1]) : null;
    const pid = packageMatch
        ? Number(packageMatch[1])
        : (state.account?.pid ?? state.event?.pid ?? null);
    const scope: 'account' | 'event' = eid ? 'event' : 'account';
    // The session's discriminator value is the eventType for now — its first implementation.
    const eventType = state.aiAgent?.sessionDiscriminator
        ?? (eid ? state.event?.type : null)
        ?? null;
    return {
        eid,
        pid,
        scope,
        currentRoute: pathname,
        formId: state.aiAgent?.activeFormId ?? null,
        eventType,
        sessionIntent: state.aiAgent?.sessionIntent ?? null,
        currentFormValues: state.aiAgent?.currentFormValues ?? null,
        recordCreated: !!eid && state.aiAgent?.createdDraftEid === eid,
    };
};
